import _ from 'lodash'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import gameIframe from '@ps/bridge/iframe/iframe'
import {
  isGroup,
  isVideo,
  isQuickLayout,
  isDragTargetArea,
  isDragBound,
  isImage,
  isParticleParent,
  isSequenceFrame,
  isParticle,
  isDragonBone,
  isText,
  isEmptyNode,
  isDrawLineNode,
  isCarouselNode,
  isEraseNode,
  isTempNode,
  isHaloNode,
  isEraseBrushCategory,
  isEraseBrushImages,
  isEraseCustomJudgeAreas,
  isMaskComponentNode,
  isQuickLayoutComponent,
} from '@ps/bridge/qc/base'
import { setUnSplitPropValue } from '@ps/bridge/qc/scripts/editor'
import store from '@ps/store'
import nodePool from '@ps/bridge/qc/node-pool'
import {
  isFollowElementNode,
  isMaskGroupNode,
  isMaskShapeGroupNode,
  isMaskShapeNode,
  isMaskedGroupNode,
} from '@ps/workbench/undo-redo/module/mask'
import i18n from '@ps/lang'

export function getNewUniqCopyName(copyName) {
  // 获取所有场景子节点名称
  const allNodesName = getDescendants(gameIframe.UIRoot, null, false).map(
    (item) => item.name,
  )

  let nameIndex = 0
  const generateNewName = (name, list) => {
    const tempName = `${name}_Copy${nameIndex > 0 ? nameIndex : ''}`
    if (list.some((val) => val === tempName)) {
      nameIndex = nameIndex + 1
      return generateNewName(name, list)
    }
    return tempName
  }

  return generateNewName(copyName, allNodesName)
}

/**
 * 根据元素树生成图层倒序树，树节点字段精简处理
 */
export function getReverseSceneTree(treeNodes, list) {
  const tempList = list || []
  if (!treeNodes || !treeNodes.length) return treeNodes
  for (let i = 0, len = treeNodes.length; i < len; i++) {
    const childs = treeNodes[i].children.filter((item) => !isTempNode(item))
    tempList[i] = {
      ..._.pick(treeNodes[i], ['uuid', 'name', 'visible']),
      parent: {
        uuid: treeNodes[i].parent.uuid,
      },
      children: [],
    }
    // 检查是否包含自研素材Halo组件，屏蔽Halo组件的子节点，屏蔽棋盘子节点
    if (
      childs &&
      childs.length > 0 &&
      !isHaloNode(treeNodes[i]) &&
      !isDrawLineNode(treeNodes[i]) &&
      !isEraseNode(treeNodes[i]) &&
      !isCarouselNode(treeNodes[i]) &&
      !isMaskComponentNode(treeNodes[i]) &&
      !isQuickLayoutComponent(treeNodes[i])
    ) {
      getReverseSceneTree(childs, tempList[i].children)
    }
  }
  return tempList.reverse()
}
export function i18nLayerName(uuid, name) {
  const node = nodePool.find(uuid)
  if (!node) return name
  if (isMaskedGroupNode(node)) {
    return i18n.t('workbench.mask.layer.maskedGroup')
  } else if (isMaskShapeNode(node)) {
    return i18n.t('workbench.mask.layer.maskShape')
  } else if (isMaskShapeGroupNode(node)) {
    return i18n.t('workbench.mask.layer.maskShapeGroup')
  } else if (isFollowElementNode(node)) {
    return i18n.t('workbench.mask.layer.followGroup')
  } else if (isMaskGroupNode(node)) {
    const index = node.name.split(' ')[1]
    return i18n.t('workbench.mask.layer.maskGroup') + ' ' + index
  }
  return name
}
// 事件响应对象节点树筛选逻辑
export function getReverseSceneTreeForEvent(treeNodes, list) {
  const tempList = list || []
  if (!treeNodes || !treeNodes.length) return treeNodes
  for (let i = 0, len = treeNodes.length; i < len; i++) {
    const childs = treeNodes[i].children.filter(
      (item) =>
        !isTempNode(item) &&
        !isEraseBrushCategory(item) && // 擦除组件屏蔽的节点
        !isEraseBrushImages(item) && // 擦除组件屏蔽的节点
        !isEraseCustomJudgeAreas(item) && // 擦除组件屏蔽的节点
        !isMaskShapeNode(item.parent), // 屏蔽遮罩组件里的形状子节点
    )
    tempList[i] = {
      ..._.pick(treeNodes[i], ['uuid', 'name', 'visible']),
      parent: {
        uuid: treeNodes[i].parent.uuid,
      },
      children: [],
    }
    // 国际化翻译
    const tname = i18nLayerName(tempList[i].uuid, tempList[i].name)
    tempList[i].name = tname
    // 检查是否包含自研素材Halo组件，屏蔽Halo组件的子节点，屏蔽棋盘子节点
    if (
      childs &&
      childs.length > 0 &&
      !isHaloNode(treeNodes[i]) &&
      !isQuickLayout(treeNodes[i]) &&
      !isDrawLineNode(treeNodes[i])
    ) {
      getReverseSceneTreeForEvent(childs, tempList[i].children)
    }
  }
  // 遮罩组不倒转数组
  if (treeNodes.every((node) => isMaskGroupNode(node))) {
    return tempList
  }
  return tempList.reverse()
}

export function getSceneTreeLevel(treeNodes, level) {
  // 思路：使用数组遍历的方式，消减树的层级 https://www.cnblogs.com/johnv/p/15765621.html
  const stack = [treeNodes]
  let depth = 0
  while (stack.length > 0) {
    let temp = []
    const list = stack.pop()

    if (list.length === 0) {
      // eslint-disable-next-line
      continue
    }

    depth++
    list.forEach((item) => {
      if (item && Array.isArray(item.children)) {
        temp = temp.concat(...item.children)
      }
    })

    if (temp.length > 0) {
      stack.push(temp)
    }
  }
  return depth
}

/**
 *  根据场景树节点，排序元素
 * @param nodes 需要排序节点
 * @param trees 当前场景节点children
 * @returns
 */
export function sortNodeByReverseTree(nodes, trees) {
  // 获取当前场景树节点
  const layerList = getReverseSceneTree(trees)
  // 生成排序树节点列表
  const sortList = []
  const sortLayerNode = (list) => {
    list.forEach((el) => {
      sortList.push(el)
      if (el.children.length) {
        sortLayerNode(el.children)
      }
    })
  }
  sortLayerNode(layerList)
  const compare = () => {
    return (a, b) => {
      const val1 = sortList.findIndex((item) => item.uuid === a.uuid)
      const val2 = sortList.findIndex((item) => item.uuid === b.uuid)
      return val1 - val2
    }
  }
  return nodes.sort(compare())
}

// 节点变更，重置拆分属性
export function resetSplitProp(node) {
  const languageList = store.state.workspace.language.languageList
  const isReset =
    isGroup(node) ||
    isImage(node) ||
    isSequenceFrame(node) ||
    isText(node) ||
    isParticleParent(node) ||
    isParticle(node) ||
    isVideo(node) ||
    isDragTargetArea(node) ||
    isDragBound(node) ||
    isDragonBone(node) ||
    isEmptyNode(node) ||
    isQuickLayout(node) ||
    isDrawLineNode(node)
  if (!isReset) return
  const resetList = ['visible', 'pivot', 'rotation'] // 默认不拆分属性
  if (isText(node)) {
    languageList.forEach((lang) => {
      resetList.push(`fontSize${lang}`)
    })
  }
  resetList.forEach((val) => {
    setUnSplitPropValue(node, val, true)
  })
}
